import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Footer } from '../../components/Footer';
import { LiveTVPanel } from '../../components/LiveTVPanel';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { fetchPunyaSummary } from '../../lib/punyaRewards';
import { DailyQuickLookSection } from '../../components/DailyQuickLookSection';
import {
  Sparkles, Star, Sun, Calendar, TrendingUp, Heart,
  Crown, ArrowRight, Check, Moon, Gift, Trophy, Coins
} from 'lucide-react';

const ZODIAC_SIGNS = [
  { id: 'aries',       symbol: '♈', name: 'Aries',       dates: 'Mar 21 - Apr 19', element: 'Fire'  },
  { id: 'taurus',      symbol: '♉', name: 'Taurus',      dates: 'Apr 20 - May 20', element: 'Earth' },
  { id: 'gemini',      symbol: '♊', name: 'Gemini',      dates: 'May 21 - Jun 20', element: 'Air'   },
  { id: 'cancer',      symbol: '♋', name: 'Cancer',      dates: 'Jun 21 - Jul 22', element: 'Water' },
  { id: 'leo',         symbol: '♌', name: 'Leo',         dates: 'Jul 23 - Aug 22', element: 'Fire'  },
  { id: 'virgo',       symbol: '♍', name: 'Virgo',       dates: 'Aug 23 - Sep 22', element: 'Earth' },
  { id: 'libra',       symbol: '♎', name: 'Libra',       dates: 'Sep 23 - Oct 22', element: 'Air'   },
  { id: 'scorpio',     symbol: '♏', name: 'Scorpio',     dates: 'Oct 23 - Nov 21', element: 'Water' },
  { id: 'sagittarius', symbol: '♐', name: 'Sagittarius', dates: 'Nov 22 - Dec 21', element: 'Fire'  },
  { id: 'capricorn',   symbol: '♑', name: 'Capricorn',   dates: 'Dec 22 - Jan 19', element: 'Earth' },
  { id: 'aquarius',    symbol: '♒', name: 'Aquarius',    dates: 'Jan 20 - Feb 18', element: 'Air'   },
  { id: 'pisces',      symbol: '♓', name: 'Pisces',      dates: 'Feb 19 - Mar 20', element: 'Water' },
];

const FEATURES = [
  { icon: Sun,       title: 'Daily Horoscope',     description: 'Start every morning with personalised cosmic guidance. Your stars, refreshed at midnight.',                        free: true,  color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { icon: Calendar,  title: 'Weekly Forecast',      description: 'Plan ahead with a full week of astrological insights covering love, career, and wellness.',                        free: true,  color: 'text-blue-500',  bg: 'bg-blue-500/10'  },
  { icon: TrendingUp,title: 'Monthly Outlook',      description: 'See the big picture. Navigate major planetary transits and seize the month\'s best moments.',                      free: true,  color: 'text-green-500', bg: 'bg-green-500/10' },
  { icon: Star,      title: 'Birth Chart Analysis', description: 'A deep Vedic astrology report built from your exact birth time, date, and location.',                             free: false, color: 'text-gold',      bg: 'bg-gold/10'      },
  { icon: Heart,     title: 'Kundali Milan',         description: 'Compatibility scoring for couples. Guna matching, doshas, and full relationship analysis.',                        free: false, color: 'text-pink-500',  bg: 'bg-pink-500/10'  },
  { icon: Crown,     title: 'Brihat Kundli Pro',    description: 'The most comprehensive Vedic report -- 40+ pages covering every aspect of your destiny.',                          free: false, color: 'text-purple-500',bg: 'bg-purple-500/10'},
];

const TESTIMONIALS = [
  { name: 'Ananya S.', sign: '♏', signName: 'Scorpio',     text: 'The daily horoscope is uncannily accurate. It\'s the first thing I check every morning before work.', stars: 5 },
  { name: 'Rohan M.',  sign: '♌', signName: 'Leo',         text: 'My birth chart report gave me chills. Details about my personality I\'ve never seen described so precisely.', stars: 5 },
  { name: 'Priya K.',  sign: '♎', signName: 'Libra',       text: 'Used Kundali Milan before our engagement. It matched our horoscopes and gave us real peace of mind.', stars: 5 },
  { name: 'Vikram T.', sign: '♐', signName: 'Sagittarius', text: 'Beautifully designed and genuinely insightful. The weekly forecast helps me plan my week ahead.', stars: 5 },
];

const PLANS = [
  {
    name: 'Free', price: '₹0', period: 'forever', description: 'Start your cosmic journey', highlight: false, cta: 'Get Started Free',
    ctaAction: 'register', navigateTo: '/register',
    features: ['Daily horoscope for your sign', 'Weekly forecast', 'Monthly outlook', 'Free Kundali Milan score', 'Cosmic Blog access'],
  },
  {
    name: 'Ask 1 Question', price: '₹99', period: 'one-time', description: 'KP precision answer to any life question', highlight: false, cta: 'Ask Now',
    ctaAction: 'navigate', navigateTo: '/ask-question',
    features: ['One specific life question', 'KP astrology engine', 'Ruling planet analysis', 'Personalised remedy', 'Instant report'],
  },
  {
    name: 'Premium Monthly', price: '₹1,599', period: 'per month', description: 'Full access to all Vedic reports', highlight: true, cta: 'Start Premium',
    ctaAction: 'pay',
    features: ['Unlimited Birth Chart reports', 'Unlimited Kundali Milan', 'Love & Relationship report', 'PDF downloads', 'Priority report generation', 'Social sharing'],
  },
  {
    name: 'Career Plus', price: '₹3,999', period: 'one-time', description: 'Deep career intelligence report', highlight: false, cta: 'Get Report',
    ctaAction: 'navigate', navigateTo: '/career-plus',
    features: ['Career archetype analysis', 'Promotion probability forecast', 'Opportunity window timeline', 'Financial growth cycles', 'Workplace risk detection', 'KP + Parashari engines'],
  },
  {
    name: 'Brihat Kundli Pro', price: '₹1,499', period: 'one-time', description: 'The ultimate Vedic life report', highlight: false, cta: 'Get Your Report',
    ctaAction: 'navigate', navigateTo: '/brihat-kundli',
    features: ['40+ page comprehensive report', 'Full planetary dasha analysis', 'Career, love & wealth insights', 'Remedies & gemstone guidance', 'PDF download included'],
  },
];

const PUNYA_WHEEL_SEGMENTS = [
  { label: '10% Off', color: '#c89d42' },
  { label: 'Tarot', color: '#8f5f2a' },
  { label: '+50', color: '#f2d089' },
  { label: 'Numerology', color: '#7d5730' },
  { label: '+100', color: '#ddba71' },
  { label: '20% Off', color: '#a06f30' },
  { label: 'Birth Chart', color: '#f5dfab' },
  { label: 'Blessing', color: '#6e4e1f' },
];

const StarField = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.4 + 0.2,
      speed: Math.random() * 0.008 + 0.002,
      phase: Math.random() * Math.PI * 2,
    }));
    const draw = (t) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        const a = 0.3 + 0.7 * Math.abs(Math.sin(t * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(197,160,89,${a * 0.85})`;
        ctx.fill();
      });
      animFrame = requestAnimationFrame(draw);
    };
    animFrame = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animFrame); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.6 }} />;
};

const TAU = Math.PI * 2;

function polarToCartesian(cx, cy, radius, angleRadians) {
  return {
    x: cx + radius * Math.cos(angleRadians),
    y: cy + radius * Math.sin(angleRadians),
  };
}

function buildArcPath(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
}

function PunyaTeaserWheel() {
  const size = 280;
  const center = size / 2;
  const radius = 126;

  return (
    <div className="relative mx-auto w-[280px] max-w-full">
      <div className="absolute left-1/2 top-[-16px] z-20 h-0 w-0 -translate-x-1/2 border-l-[14px] border-r-[14px] border-b-[24px] border-l-transparent border-r-transparent border-b-gold" />
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="h-[280px] w-[280px] drop-shadow-[0_16px_40px_rgba(0,0,0,0.18)]"
        role="img"
        aria-label="Punya Rewards preview wheel"
      >
        <circle cx={center} cy={center} r={radius + 10} fill="rgba(201,150,31,0.08)" stroke="rgba(201,150,31,0.28)" strokeWidth="8" />
        {PUNYA_WHEEL_SEGMENTS.map((segment, index) => {
          const startAngle = -Math.PI / 2 + (index / PUNYA_WHEEL_SEGMENTS.length) * TAU;
          const endAngle = -Math.PI / 2 + ((index + 1) / PUNYA_WHEEL_SEGMENTS.length) * TAU;
          const labelAngle = startAngle + (endAngle - startAngle) / 2;
          const labelPoint = polarToCartesian(center, center, 78, labelAngle);
          return (
            <g key={segment.label}>
              <path d={buildArcPath(center, center, radius, startAngle, endAngle)} fill={segment.color} stroke="rgba(255,255,255,0.22)" strokeWidth="1.25" />
              <text
                x={labelPoint.x}
                y={labelPoint.y}
                fill={index % 2 === 0 ? '#2d1e0d' : '#fff7eb'}
                fontSize="11"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${(labelAngle * 180) / Math.PI + 90} ${labelPoint.x} ${labelPoint.y})`}
              >
                {segment.label}
              </text>
            </g>
          );
        })}
        <circle cx={center} cy={center} r="34" fill="rgba(18,12,7,0.94)" stroke="rgba(201,150,31,0.35)" strokeWidth="2.5" />
        <circle cx={center} cy={center} r="18" fill="rgba(201,150,31,0.12)" />
        <text x={center} y={center - 4} fill="#f2d089" fontSize="18" fontWeight="700" textAnchor="middle">✦</text>
        <text x={center} y={center + 14} fill="#f6e8ca" fontSize="9" fontWeight="700" textAnchor="middle" letterSpacing="1.6">PUNYA</text>
      </svg>
    </div>
  );
}

export const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSign, setSelectedSign] = useState(null);
  const [visible, setVisible] = useState(new Set());
  const [punyaBalance, setPunyaBalance] = useState(null);
  const refs = useRef({});

  // Landing page is accessible to all users -- logged-in users can view it too

  useEffect(() => {
    const saved = localStorage.getItem('selected-sign');
    if (saved) setSelectedSign(saved);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user?.email) {
      setPunyaBalance(null);
      return undefined;
    }

    fetchPunyaSummary()
      .then((response) => {
        if (!cancelled) setPunyaBalance(response?.balance ?? 0);
      })
      .catch(() => {
        if (!cancelled) setPunyaBalance(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setVisible((p) => new Set([...p, e.target.dataset.sec])); });
    }, { threshold: 0.1 });
    Object.values(refs.current).forEach((el) => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const ref = (key) => (el) => { refs.current[key] = el; if (el) el.dataset.sec = key; };
  const vis = (k) => visible.has(k);

  const anim = (key, delay = 0) => ({
    opacity: vis(key) ? 1 : 0,
    transform: vis(key) ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
  });

  const elementColor = (el) => ({
    Fire: 'text-amber-500', Earth: 'text-green-600', Air: 'text-blue-400', Water: 'text-cyan-500'
  }[el] || 'text-muted-foreground');

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO
        title="Free Daily Horoscope & Vedic Astrology"
        description="Get your free daily, weekly, and monthly horoscope predictions. AI-powered Birth Chart, Kundali Milan, and Brihat Kundli Pro reports based on Vedic astrology."
        url="https://www.everydayhoroscope.in"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.everydayhoroscope.in/#organization",
              "name": "Everyday Horoscope",
              "alternateName": "EverydayHoroscope",
              "url": "https://www.everydayhoroscope.in",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.everydayhoroscope.in/og-image.png",
                "width": 1200,
                "height": 630
              },
              "description": "India's premier Vedic astrology platform offering daily horoscopes, birth charts, Kundali Milan, and AI-powered astrological insights rooted in 5,000 years of Vedic wisdom.",
              "foundingDate": "2024",
              "areaServed": "IN",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@everydayhoroscope.in",
                "contactType": "customer support",
                "availableLanguage": ["English", "Hindi"]
              },
              "sameAs": [
                "https://www.facebook.com/EverydayHoroscope",
                "https://www.youtube.com/@EverydayHoroscope",
                "https://www.instagram.com/everydayhoroscope.in"
              ]
            },
            {
              "@type": "WebSite",
              "@id": "https://www.everydayhoroscope.in/#website",
              "name": "Everyday Horoscope",
              "url": "https://www.everydayhoroscope.in",
              "description": "Free daily horoscope, Vedic birth chart analysis, Kundali Milan, and premium astrology reports -- rooted in 5,000 years of ancient Vedic wisdom.",
              "publisher": {
                "@id": "https://www.everydayhoroscope.in/#organization"
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://www.everydayhoroscope.in/horoscope/daily?sign={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              },
              "inLanguage": "en-IN"
            }
          ]
        }}
      />
      <LiveTVPanel />

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Sparkles className="h-5 w-5 text-gold" />
            <span className="font-cinzel font-bold text-lg tracking-wide">Everyday Horoscope</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            {[['Features', 'features'], ['Pricing', null], ['Blog', null]].map(([label, id]) => (
              <button key={label} onClick={() => {
                if (label === 'Pricing') navigate('/pricing');
                else if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                else navigate('/blog');
              }} className="hover:text-foreground transition-colors">{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5">Sign in</button>
            <button onClick={() => navigate('/register')} className="text-sm bg-gold hover:bg-gold/90 text-primary-foreground px-4 py-1.5 rounded-sm font-medium transition-all">Get Started</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-16 pb-12 px-4 overflow-hidden">
        <StarField />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(197,160,89,0.07) 0%, transparent 70%)' }} />
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 text-gold text-xs font-semibold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-8">
            <Moon className="h-3 w-3" /> Ancient Vedic Astrology
          </div>
          <h1 className="font-cinzel font-bold leading-none mb-6" style={{ fontSize: 'clamp(2.8rem, 8vw, 6rem)' }}>
            Your Stars.<br /><span className="text-gold">Your Story.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-playfair">
            Personalised daily horoscopes, birth charts, and Vedic reports -- rooted in 5,000 years of ancient wisdom, interpreted for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button onClick={() => document.getElementById('sign-picker')?.scrollIntoView({ behavior: 'smooth' })} className="group inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-primary-foreground px-8 py-4 rounded-sm font-semibold text-base transition-all hover:shadow-[0_8px_30px_-5px_rgba(197,160,89,0.5)] hover:-translate-y-0.5">
              Begin Your Journey <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="inline-flex items-center gap-2 border border-border hover:border-gold/50 text-foreground px-8 py-4 rounded-sm font-medium text-base transition-all hover:-translate-y-0.5">
              Explore Features
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5"><span className="text-gold text-xs">★★★★★</span><span>4.9 / 5</span></div>
            <div className="h-4 w-px bg-border" />
            <span>10,000+ readings generated</span>
            <div className="h-4 w-px bg-border hidden sm:block" />
            <span className="hidden sm:block">12 zodiac signs covered daily</span>
          </div>
        </div>
      </section>

      {/* SIGN PICKER */}
      <section id="sign-picker" ref={ref('signs')} className="py-20 px-4" style={anim('signs')}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold mb-3">What's your sign?</h2>
            <p className="text-muted-foreground">Select your zodiac to personalise your experience</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-10">
            {ZODIAC_SIGNS.map((sign) => (
              <button key={sign.id} onClick={() => { setSelectedSign(sign.id); localStorage.setItem('selected-sign', sign.id); }}
                className={`group flex flex-col items-center gap-1.5 p-4 rounded-sm border transition-all duration-300 hover:-translate-y-1 ${selectedSign === sign.id ? 'border-gold bg-gold/10 shadow-[0_0_20px_-5px_rgba(197,160,89,0.4)]' : 'border-border bg-card hover:border-gold/40'}`}>
                <span className="text-3xl leading-none group-hover:scale-110 transition-transform duration-200">{sign.symbol}</span>
                <span className="text-xs font-semibold font-playfair">{sign.name}</span>
                <span className={`text-[10px] uppercase tracking-wider ${elementColor(sign.element)}`}>{sign.element}</span>
              </button>
            ))}
          </div>
          {selectedSign && (
            <div className="text-center">
              <button onClick={() => navigate('/register')} className="group inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-primary-foreground px-10 py-4 rounded-sm font-semibold text-base transition-all hover:shadow-[0_8px_30px_-5px_rgba(197,160,89,0.4)] hover:-translate-y-0.5">
                <Sparkles className="h-4 w-4" />
                Read My {ZODIAC_SIGNS.find(s => s.id === selectedSign)?.name} Horoscope
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* DAILY QUICK LOOK */}
      <DailyQuickLookSection />

      {/* FEATURES */}
      <section id="features" ref={ref('features')} className="py-20 px-4 border-t border-border/50" style={anim('features', 100)}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-4"><Star className="h-3 w-3" /> Everything you need</div>
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold mb-4">Cosmic tools for every seeker</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">From free daily readings to in-depth Vedic reports -- insights for every stage of your journey.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} onClick={() => navigate('/register')} className="group relative p-6 rounded-sm border border-border bg-card hover:border-gold/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_-5px_rgba(197,160,89,0.15)] cursor-pointer">
                  {!f.free && <div className="absolute top-4 right-4 flex items-center gap-1 bg-gold/15 text-gold text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"><Crown className="h-2.5 w-2.5" /> Premium</div>}
                  <div className={`${f.bg} ${f.color} w-11 h-11 rounded-sm flex items-center justify-center mb-4`}><Icon className="h-5 w-5" /></div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 group-hover:text-gold transition-colors">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                  {f.free && <div className="mt-4 text-xs text-green-500 font-semibold uppercase tracking-wider flex items-center gap-1"><Check className="h-3 w-3" /> Free forever</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PUNYA REWARDS */}
      <section ref={ref('punya')} className="py-20 px-4 border-t border-border/50" style={anim('punya', 100)}>
        <div className="mx-auto max-w-6xl">
          <div className="overflow-hidden rounded-[2rem] border border-gold/20 bg-[radial-gradient(circle_at_top_left,rgba(201,150,31,0.16),transparent_28%),linear-gradient(140deg,rgba(201,150,31,0.06),rgba(17,12,8,0.96))] px-6 py-8 shadow-[0_24px_70px_rgba(28,20,12,0.18)] sm:px-8 lg:px-10">
            <div className="grid gap-10 lg:grid-cols-[1.15fr,0.85fr] lg:items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                  <Coins className="h-3.5 w-3.5" />
                  Punya Rewards
                </div>
                <div className="space-y-3">
                  <h2 className="font-playfair text-3xl font-semibold text-white sm:text-4xl">
                    Earn spiritual merit while you practice.
                  </h2>
                  <p className="max-w-2xl text-sm leading-7 text-[#d9c8af] sm:text-base">
                    Every reading, chart, and return visit can build your Punya Points balance. Spin the wheel for discounts, bonus points, and premium blessings while you rise through the weekly leaderboard.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  {[
                    {
                      icon: Sparkles,
                      title: 'Earn',
                      copy: 'Points flow in from the Temple modules you already use every day.',
                    },
                    {
                      icon: Gift,
                      title: 'Spin',
                      copy: 'Turn your points into discounts, free reports, and Daily Blessing rewards.',
                    },
                    {
                      icon: Trophy,
                      title: 'Rise',
                      copy: 'Weekly leaders climb the board and unlock extra attention from the wheel.',
                    },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <Card key={item.title} className="rounded-2xl border border-gold/20 bg-gold/[0.04] p-5 text-left shadow-sm">
                        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 bg-gold/10 text-gold">
                          <Icon className="h-4 w-4" />
                        </div>
                        <h3 className="font-playfair text-xl font-semibold text-white">{item.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-[#d6c3a8]">{item.copy}</p>
                      </Card>
                    );
                  })}
                </div>

                {user?.email ? (
                  <div className="flex flex-col gap-4 rounded-2xl border border-gold/20 bg-gold/[0.05] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold/80">Temple wallet</p>
                      <p className="mt-1 text-2xl font-semibold text-white">
                        {punyaBalance ?? '...'} Punya Points
                      </p>
                      <p className="mt-1 text-sm text-[#d8c7ae]">Your next spin is waiting inside the rewards page.</p>
                    </div>
                    <Button onClick={() => navigate('/punya-rewards')} className="bg-gold text-primary-foreground hover:bg-gold/90">
                      Spin Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 rounded-2xl border border-gold/20 bg-gold/[0.05] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold/80">Start earning</p>
                      <p className="mt-1 text-xl font-semibold text-white">Sign in to start collecting Punya Points.</p>
                      <p className="mt-1 text-sm text-[#d8c7ae]">Guest visitors can preview the wheel. Logged-in seekers earn from the Temple.</p>
                    </div>
                    <Button onClick={() => navigate('/login')} className="bg-gold text-primary-foreground hover:bg-gold/90">
                      Create Free Account
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <PunyaTeaserWheel />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section ref={ref('testimonials')} className="py-20 px-4 border-t border-border/50" style={anim('testimonials', 100)}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-4"><Heart className="h-3 w-3" /> Loved by seekers</div>
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold">What our community says</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-6 rounded-sm border border-border bg-card flex flex-col gap-4">
                <div className="flex gap-0.5">{[...Array(t.stars)].map((_, j) => <span key={j} className="text-gold text-sm">★</span>)}</div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 font-playfair italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center text-lg leading-none">{t.sign}</div>
                  <div><p className="text-sm font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{t.signName}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" ref={ref('pricing')} className="py-20 px-4 border-t border-border/50" style={anim('pricing', 100)}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-gold text-xs font-semibold uppercase tracking-[0.2em] mb-4"><Crown className="h-3 w-3" /> Simple pricing</div>
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold mb-4">Start free. Go deeper when ready.</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">No hidden fees. Cancel anytime. Your cosmic journey begins for free.</p>
          </div>
          {/* Row 1: Featured plan centred on its own */}
          {PLANS.filter(p => p.highlight).map((plan) => (
            <div key={plan.name} className="relative p-6 rounded-sm border border-gold bg-gold/5 shadow-[0_0_40px_-10px_rgba(197,160,89,0.3)] flex flex-col gap-5 mb-6 max-w-sm mx-auto w-full">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full whitespace-nowrap">Most Popular</div>
              <div>
                <h3 className="font-cinzel font-bold text-lg mb-1">{plan.name}</h3>
                <p className="text-xs text-muted-foreground">{plan.description}</p>
              </div>
              <div className="flex items-end gap-1">
                <span className="text-3xl font-bold font-playfair">{plan.price}</span>
                <span className="text-sm text-muted-foreground mb-1">/ {plan.period}</span>
              </div>
              <ul className="space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/register')} className="w-full py-3 rounded-sm font-semibold text-sm bg-gold hover:bg-gold/90 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-5px_rgba(197,160,89,0.4)]">
                {plan.cta}
              </button>
            </div>
          ))}
          {/* Row 2: 4 secondary plans in a balanced 2×2 grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.filter(p => !p.highlight).map((plan) => (
              <div key={plan.name} className="relative p-6 rounded-sm border border-border bg-card flex flex-col gap-5 transition-all hover:border-gold/40 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_-5px_rgba(197,160,89,0.1)]">
                <div>
                  <h3 className="font-cinzel font-bold text-base mb-1">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground">{plan.description}</p>
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-2xl font-bold font-playfair">{plan.price}</span>
                  <span className="text-xs text-muted-foreground mb-1">/ {plan.period}</span>
                </div>
                <ul className="space-y-2 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <Check className="h-3.5 w-3.5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate(plan.navigateTo || '/register')} className="w-full py-2.5 rounded-sm font-semibold text-xs border border-border hover:border-gold/50 text-foreground transition-all hover:-translate-y-0.5">
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section ref={ref('cta')} className="py-24 px-4 border-t border-border/50 relative overflow-hidden" style={anim('cta')}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(197,160,89,0.06) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <Sparkles className="h-10 w-10 text-gold mx-auto mb-6 opacity-80" />
          <h2 className="font-cinzel font-bold text-3xl md:text-4xl mb-5 leading-snug">The stars are ready.<br />Are you?</h2>
          <p className="text-muted-foreground mb-8 text-lg font-playfair">Join thousands of seekers discovering their cosmic path every day.</p>
          <button onClick={() => navigate('/register')} className="group inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-primary-foreground px-10 py-4 rounded-sm font-semibold text-base transition-all hover:shadow-[0_8px_30px_-5px_rgba(197,160,89,0.5)] hover:-translate-y-0.5">
            Create Free Account <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-xs text-muted-foreground">No credit card required · Free forever plan available</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
